import { taskApi } from '../config/apiClient';

// ============================================================================
// taskDataService — gts_task_api (SQL) icin tek REST yuzeyi.
//
// Faz 5 (Gorev Takip SQL'e) frontend veri katmani. RTDB `companies/{cid}/...`
// gorev agacinin yerine gecer. companyId/Authorization header'lari apiClient
// interceptor'i tarafindan (Firebase token claim + X-Company-Id) otomatik eklenir;
// bu serviste companyId GONDERILMEZ.
//
// Backend (System.Text.Json) camelCase doner; tipler camelCase tutulur.
// Uclar: TasksController (/api/task/tasks), TaskRecurrenceController (/api/task),
// TaskGroupsController (/api/task/task-groups), TeamGroupsController
// (/api/task/team-groups), TeamProjectsController (/api/task/team-projects),
// YearlyTaskPlansController (/api/task/yearly-plans).
//
// NOT (RTDB → SQL kritik farklar):
//  - id: RTDB push-key (string) yerine SQL int. Tum id'ler number.
//  - repetitionTimes / imageUrls: backend'de opak string (JSON tercih). Bu
//    serviste parse/stringify yardimcilari (parseStringArray/stringifyStringArray).
//  - branchesId: RTDB'de string | null | {[id]:boolean} olabilir; SQL tek string.
//    normalizeBranchesId yardimcisi ile tek string'e indir.
//  - tarih: API ISO datetime (createdAt/deadline/...) ve completions'ta
//    taskDate = 'yyyy-MM-dd', taskTime = 'HH:mm'.
// ============================================================================

// ----------------------------------------------------------------------------
// Tipler — Gorev (TaskItem)
// ----------------------------------------------------------------------------
export type TaskType = 'single' | 'daily' | 'weekly' | 'monthly';
export type TaskStatus =
  | 'pending' | 'accepted' | 'started' | 'completed' | 'rejected';
export type CompletionType = 'button' | 'qr' | string;

export interface TaskDto {
  id: number;
  companyId: string;
  taskType: string;
  name: string;
  description?: string | null;
  personnelFirebaseUid?: string | null;
  branchesId?: string | null;
  groupId?: number | null;
  status: string;
  isRecurring: boolean;
  completionType?: string | null;
  startTolerance?: number | null;
  dailyRepetitions?: number | null;
  /** Opak string (JSON dizi tercih). parseStringArray ile diziye cevir. */
  repetitionTimes?: string | null;
  requestCompletionPhoto: boolean;
  canBeRejected: boolean;
  deadline?: string | null;
  startDate?: string | null;
  /** Opak string (JSON dizi tercih). parseStringArray ile diziye cevir. */
  imageUrls?: string | null;
  acceptedAt?: string | null;
  startedAt?: string | null;
  startedByUid?: string | null;
  completedAt?: string | null;
  completedByUid?: string | null;
  completionPhotoUrl?: string | null;
  wasDelayed?: boolean | null;
  delayReason?: string | null;
  rejectedAt?: string | null;
  rejectedByUid?: string | null;
  rejectReason?: string | null;
  renewedAt?: string | null;
  createdByUid?: string | null;
  legacyFirebaseKey?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TaskListParams {
  taskType?: string;
  status?: string;
  personnelId?: string;
  branchesId?: string;
  groupId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface TaskListResult {
  items: TaskDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TaskCreateInput {
  taskType?: string;
  name: string;
  description?: string | null;
  personnelFirebaseUid?: string | null;
  branchesId?: string | null;
  groupId?: number | null;
  isRecurring?: boolean;
  completionType?: string | null;
  startTolerance?: number | null;
  dailyRepetitions?: number | null;
  repetitionTimes?: string | null;
  requestCompletionPhoto?: boolean;
  canBeRejected?: boolean;
  deadline?: string | null;
  startDate?: string | null;
  imageUrls?: string | null;
  createdByUid?: string | null;
  legacyFirebaseKey?: string | null;
}

export type TaskUpdateInput = Partial<Omit<
  TaskCreateInput,
  'taskType' | 'personnelFirebaseUid' | 'createdByUid' | 'legacyFirebaseKey'
>>;

export type TaskStatusAction = 'accept' | 'start' | 'complete' | 'reject' | 'renew';

export interface TaskStatusInput {
  action: TaskStatusAction;
  byUid?: string | null;
  completionPhotoUrl?: string | null;
  wasDelayed?: boolean | null;
  delayReason?: string | null;
  rejectReason?: string | null;
}

export interface TaskCommentDto {
  id: number;
  taskId: number;
  text: string;
  userUid?: string | null;
  userName?: string | null;
  createdAt: string;
}

// ----------------------------------------------------------------------------
// Tipler — Tekrar slotu + tamamlama
// ----------------------------------------------------------------------------
export interface SlotDto {
  id: number;
  taskId: number;
  dayOfWeek?: number | null;
  monthNumber?: number | null;
  dayOfMonth?: number | null;
  dailyRepetitions?: number | null;
  repetitionTimes?: string | null;
}

export interface SlotInput {
  dayOfWeek?: number | null;
  monthNumber?: number | null;
  dayOfMonth?: number | null;
  dailyRepetitions?: number | null;
  repetitionTimes?: string | null;
}

export type CompletionStatus = 'started' | 'completed' | 'missed';

export interface CompletionDto {
  id: number;
  taskId: number;
  taskType?: string | null;
  taskDate: string; // yyyy-MM-dd
  taskTime: string; // HH:mm
  status: string;
  startedAt?: string | null;
  completedAt?: string | null;
  missedAt?: string | null;
  missedReason?: string | null;
  personnelFirebaseUid?: string | null;
  personnelFirstName?: string | null;
  personnelLastName?: string | null;
  weekDay?: number | null;
  completionPhotoUrl?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  locationAccuracy?: number | null;
  devicePlatform?: string | null;
  deviceUserAgent?: string | null;
  startTolerance?: number | null;
  createdAt: string;
}

/** Sirket geneli tamamlama satiri (admin Tamamlanan/Kacirilan) — gorevden denormalize alanlar dahil. */
export interface CompanyCompletionDto extends CompletionDto {
  taskName?: string | null;
  taskDescription?: string | null;
  taskBranchesId?: string | null;
  taskDeadline?: string | null;
  taskStartDate?: string | null;
}

export interface CompanyCompletionParams {
  status?: 'started' | 'completed' | 'missed' | 'all';
  dateFrom?: string;
  dateTo?: string;
  personnelFirebaseUid?: string;
  taskType?: string;
  branchesId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CompletionStartInput {
  taskTime: string; // HH:mm (zorunlu)
  taskDate?: string; // yyyy-MM-dd (yoksa bugun)
  personnelFirebaseUid?: string | null;
  personnelFirstName?: string | null;
  personnelLastName?: string | null;
  startTolerance?: number | null;
}

export interface CompletionCompleteInput {
  completionPhotoUrl?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  locationAccuracy?: number | null;
  devicePlatform?: string | null;
  deviceUserAgent?: string | null;
  personnelFirebaseUid?: string | null;
  personnelFirstName?: string | null;
  personnelLastName?: string | null;
}

export interface SlotStatus {
  time: string;
  status: string; // TaskTimeStatus: notYetDue|approaching|active|missed|completed|started
}

export interface TaskStatusResult {
  taskId: number;
  taskType: string;
  date: string;
  startTolerance: number;
  slots: SlotStatus[];
  records: CompletionDto[];
}

// ----------------------------------------------------------------------------
// Tipler — Gorev grubu
// ----------------------------------------------------------------------------
export interface TaskGroupDto {
  id: number;
  name: string;
  branchesId?: string | null;
  legacyFirebaseKey?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * RTDB-uyumlu grup sekli. `id` = legacyFirebaseKey (yoksa String(sqlId)) →
 * mevcut RTDB gorevlerinin `groupId` (firebase key) eslesmesi/yazimi korunur.
 * `sqlId` SQL uzerinde update/delete icin kullanilir.
 */
export interface LegacyTaskGroup {
  id: string;
  sqlId: number;
  name: string;
  createdAt: string;
  branchesId: string | null;
}

// ----------------------------------------------------------------------------
// Tipler — Ekip grubu + uye
// ----------------------------------------------------------------------------
export interface TeamGroupDto {
  id: number;
  name: string;
  description?: string | null;
  color?: string | null;
  memberCount: number;
  legacyFirebaseKey?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TeamMemberDto {
  id: number;
  teamGroupId: number;
  personnelFirebaseUid: string;
  name?: string | null;
  duty?: string | null;
  memberRole?: string | null;
}

export interface TeamMemberInput {
  personnelFirebaseUid: string;
  name?: string | null;
  duty?: string | null;
  memberRole?: string | null;
}

// ----------------------------------------------------------------------------
// Tipler — Ekip projesi + gorevi
// ----------------------------------------------------------------------------
export interface TeamProjectDto {
  id: number;
  companyId: string;
  teamGroupId?: number | null;
  teamName?: string | null;
  name: string;
  isSequential: boolean;
  status: string;
  activeTaskOrder?: number | null;
  groupId?: number | null;
  groupName?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  taskTotal: number;
  taskCompleted: number;
  completedAt?: string | null;
  legacyFirebaseKey?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TeamProjectTaskDto {
  id: number;
  teamProjectId: number;
  name: string;
  description?: string | null;
  teamMemberUid?: string | null;
  teamMemberName?: string | null;
  priority?: string | null;
  dueDate?: string | null;
  status: string;
  taskOrder?: number | null;
  canStart: boolean;
  imageUrls?: string | null;
  groupId?: number | null;
  branchId?: string | null;
  lastUpdatedAt?: string | null;
  lastUpdatedByUid?: string | null;
  lastUpdatedByName?: string | null;
  legacyFirebaseKey?: string | null;
  createdAt: string;
}

export interface TeamProjectDetail {
  project: TeamProjectDto;
  tasks: TeamProjectTaskDto[];
}

export interface TeamTaskStatusHistoryDto {
  id: number;
  statusKey: string;
  statusTimestamp: string;
}

// ----------------------------------------------------------------------------
// Tipler — Yillik plan
// ----------------------------------------------------------------------------
export interface YearlyPlanDto {
  id: number;
  companyId: string;
  name: string;
  description?: string | null;
  personnelFirebaseUid?: string | null;
  personnelName?: string | null;
  groupId?: number | null;
  groupName?: string | null;
  planDate?: string | null; // yyyy-MM-dd
  yearlyPlanDetails?: string | null;
  branchesId?: string | null;
  branchName?: string | null;
  status: string;
  legacyFirebaseKey?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

// ----------------------------------------------------------------------------
// Tipler — Istatistik
// ----------------------------------------------------------------------------
export interface TaskStatsResult {
  companyId: string;
  summary: {
    totalTasks: number;
    yearlyPlans: number;
    teamTasks: number;
    completed: number;
    missed: number;
    started: number;
  };
  byType: Array<{ type: string; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
  byCompletionStatus: Array<{ status: string; count: number }>;
}

// ----------------------------------------------------------------------------
// Yardimcilar
// ----------------------------------------------------------------------------

/**
 * Opak string alani (repetitionTimes/imageUrls) → string dizisi.
 * Once JSON dizi denenir; olmazsa virgulle ayrilmis kabul edilir.
 */
export function parseStringArray(value?: string | null): string[] {
  if (!value) return [];
  const s = value.trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr)) return arr.map((x) => String(x));
    } catch {
      /* JSON degil → CSV'ye dus */
    }
  }
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

/** string dizisi → JSON string (backend opak saklar). */
export function stringifyStringArray(arr?: string[] | null): string | null {
  if (!arr || arr.length === 0) return null;
  return JSON.stringify(arr);
}

/**
 * branchesId polimorfizmini (string | null | {[id]:boolean}) tek string'e indir.
 * Obje ise ilk true olan (yoksa ilk) anahtar alinir.
 */
export function normalizeBranchesId(
  value?: string | null | Record<string, boolean>
): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    const truthy = keys.find((k) => value[k]);
    return truthy ?? keys[0] ?? null;
  }
  return null;
}

/**
 * Haftalik slotlari RTDB-uyumlu `days` haritasina cevirir:
 *   { [dayOfWeek 0-6]: { repetitionTimes: string[], dailyRepetitions: number } }
 * WeeklyTasks.tsx getTaskDays(task.days) ile uyumlu.
 */
export function slotsToWeeklyDays(
  slots: SlotDto[]
): Record<string, { repetitionTimes: string[]; dailyRepetitions: number }> {
  const days: Record<string, { repetitionTimes: string[]; dailyRepetitions: number }> = {};
  slots.forEach((s) => {
    if (s.dayOfWeek == null) return;
    days[String(s.dayOfWeek)] = {
      repetitionTimes: parseStringArray(s.repetitionTimes),
      dailyRepetitions: s.dailyRepetitions ?? 1,
    };
  });
  return days;
}

/**
 * Aylik slotlari RTDB-uyumlu `month{N}.day{D}` anahtarlarina cevirir.
 * MonthlyTasks.tsx getTaskMonthDays (task icindeki `month*`/`day*` anahtarlari) ile uyumlu.
 */
export function slotsToMonthlyObject(slots: SlotDto[]): Record<string, any> {
  const obj: Record<string, any> = {};
  slots.forEach((s) => {
    if (s.monthNumber == null || s.dayOfMonth == null) return;
    const monthKey = `month${s.monthNumber}`;
    const dayKey = `day${s.dayOfMonth}`;
    if (!obj[monthKey]) obj[monthKey] = {};
    obj[monthKey][dayKey] = {
      repetitionTimes: parseStringArray(s.repetitionTimes),
      dailyRepetitions: s.dailyRepetitions ?? 1,
    };
  });
  return obj;
}

// API zarf cozucusu (success/items/data)
function unwrap<T>(data: any, key: 'items' | 'data'): T {
  return (data?.[key] ?? (key === 'items' ? [] : null)) as T;
}

// ============================================================================
// taskDataService
// ============================================================================
const TASKS = '/api/task/tasks';
const REC = '/api/task';
const GROUPS = '/api/task/task-groups';
const TEAMS = '/api/task/team-groups';
const PROJECTS = '/api/task/team-projects';
const YEARLY = '/api/task/yearly-plans';

export const taskDataService = {
  // ── Gorevler ──────────────────────────────────────────────────────────
  async listTasks(params: TaskListParams = {}): Promise<TaskListResult> {
    const { data } = await taskApi.get(TASKS, { params });
    return {
      items: data?.items ?? [],
      totalCount: data?.totalCount ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 25,
      totalPages: data?.totalPages ?? 0,
    };
  },

  async getTask(id: number): Promise<TaskDto | null> {
    const { data } = await taskApi.get(`${TASKS}/${id}`);
    return unwrap<TaskDto | null>(data, 'data');
  },

  async createTask(input: TaskCreateInput): Promise<TaskDto> {
    const { data } = await taskApi.post(`${TASKS}/create`, input);
    return unwrap<TaskDto>(data, 'data');
  },

  async updateTask(id: number, input: TaskUpdateInput): Promise<TaskDto> {
    const { data } = await taskApi.post(`${TASKS}/${id}/update`, input);
    return unwrap<TaskDto>(data, 'data');
  },

  async changeTaskStatus(id: number, input: TaskStatusInput): Promise<TaskDto> {
    const { data } = await taskApi.post(`${TASKS}/${id}/status`, input);
    return unwrap<TaskDto>(data, 'data');
  },

  async changeTaskAssignee(
    id: number,
    personnelFirebaseUid: string | null
  ): Promise<TaskDto> {
    const { data } = await taskApi.post(`${TASKS}/${id}/assignee`, { personnelFirebaseUid });
    return unwrap<TaskDto>(data, 'data');
  },

  async deleteTask(id: number): Promise<void> {
    await taskApi.post(`${TASKS}/${id}/delete`, {});
  },

  // ── Yorumlar ──────────────────────────────────────────────────────────
  async getComments(taskId: number): Promise<TaskCommentDto[]> {
    const { data } = await taskApi.get(`${TASKS}/${taskId}/comments`);
    return unwrap<TaskCommentDto[]>(data, 'items');
  },

  async addComment(
    taskId: number,
    input: { text: string; userUid?: string | null; userName?: string | null }
  ): Promise<TaskCommentDto> {
    const { data } = await taskApi.post(`${TASKS}/${taskId}/comments`, input);
    return unwrap<TaskCommentDto>(data, 'data');
  },

  // ── Istatistik ────────────────────────────────────────────────────────
  async getStats(params: {
    branchesId?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}): Promise<TaskStatsResult | null> {
    const { data } = await taskApi.get(`${TASKS}/stats`, { params });
    if (!data?.success) return null;
    return {
      companyId: data.companyId,
      summary: data.summary,
      byType: data.byType ?? [],
      byStatus: data.byStatus ?? [],
      byCompletionStatus: data.byCompletionStatus ?? [],
    };
  },

  // ── Tekrar slotlari ───────────────────────────────────────────────────
  async getSlots(taskId: number): Promise<SlotDto[]> {
    const { data } = await taskApi.get(`${REC}/tasks/${taskId}/slots`);
    return unwrap<SlotDto[]>(data, 'items');
  },

  async setSlots(taskId: number, slots: SlotInput[]): Promise<number> {
    const { data } = await taskApi.post(`${REC}/tasks/${taskId}/slots`, { slots });
    return data?.count ?? 0;
  },

  /**
   * Sirket geneli tekrar slotlari (weekly/monthly listelerinde gun-saat izgarasi).
   * GET /api/task/slots?taskType=weekly|monthly — her slot taskId ile gelir.
   */
  async listSlots(taskType?: string): Promise<SlotDto[]> {
    const { data } = await taskApi.get(`${REC}/slots`, {
      params: taskType ? { taskType } : undefined,
    });
    return unwrap<SlotDto[]>(data, 'items');
  },

  // ── Tamamlama / baslama ───────────────────────────────────────────────
  async getCompletions(taskId: number, date?: string): Promise<CompletionDto[]> {
    const { data } = await taskApi.get(`${REC}/tasks/${taskId}/completions`, {
      params: date ? { date } : undefined,
    });
    return unwrap<CompletionDto[]>(data, 'items');
  },

  async startCompletion(taskId: number, input: CompletionStartInput): Promise<CompletionDto> {
    const { data } = await taskApi.post(`${REC}/tasks/${taskId}/completions`, input);
    return unwrap<CompletionDto>(data, 'data');
  },

  /**
   * Sirket geneli tamamlama/kacirma listesi (admin Tamamlanan/Kacirilan sekmeleri).
   * GET /api/task/completions — gorevden denormalize (taskName/branchesId/...) alanlar dahil.
   */
  async listCompletions(params: CompanyCompletionParams = {}): Promise<{
    items: CompanyCompletionDto[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const { data } = await taskApi.get(`${REC}/completions`, { params });
    return {
      items: data?.items ?? [],
      totalCount: data?.totalCount ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 500,
      totalPages: data?.totalPages ?? 0,
    };
  },

  async completeCompletion(
    completionId: number,
    input: CompletionCompleteInput = {}
  ): Promise<CompletionDto> {
    const { data } = await taskApi.post(`${REC}/completions/${completionId}/complete`, input);
    return unwrap<CompletionDto>(data, 'data');
  },

  async getTaskStatus(taskId: number, date?: string): Promise<TaskStatusResult | null> {
    const { data } = await taskApi.get(`${REC}/tasks/${taskId}/status`, {
      params: date ? { date } : undefined,
    });
    if (!data?.success) return null;
    return {
      taskId: data.taskId,
      taskType: data.taskType,
      date: data.date,
      startTolerance: data.startTolerance,
      slots: data.slots ?? [],
      records: data.records ?? [],
    };
  },

  // ── Gorev gruplari ────────────────────────────────────────────────────
  async listGroups(branchesId?: string): Promise<TaskGroupDto[]> {
    const { data } = await taskApi.get(GROUPS, {
      params: branchesId ? { branchesId } : undefined,
    });
    return unwrap<TaskGroupDto[]>(data, 'items');
  },

  async getGroup(id: number): Promise<TaskGroupDto | null> {
    const { data } = await taskApi.get(`${GROUPS}/${id}`);
    return unwrap<TaskGroupDto | null>(data, 'data');
  },

  async createGroup(input: {
    name: string;
    branchesId?: string | null;
    legacyFirebaseKey?: string | null;
  }): Promise<TaskGroupDto> {
    const { data } = await taskApi.post(`${GROUPS}/create`, input);
    return unwrap<TaskGroupDto>(data, 'data');
  },

  async updateGroup(input: {
    id: number;
    name?: string | null;
    branchesId?: string | null;
  }): Promise<TaskGroupDto> {
    const { data } = await taskApi.post(`${GROUPS}/update`, input);
    return unwrap<TaskGroupDto>(data, 'data');
  },

  async deleteGroup(id: number): Promise<void> {
    await taskApi.post(`${GROUPS}/delete`, { id });
  },

  /**
   * RTDB-uyumlu grup listesi: `id` = legacyFirebaseKey (yoksa String(sqlId)).
   * Gorevler hala RTDB'de `groupId` = firebase key tuttugu icin filtre/yazma
   * uyumlulugu korunur; `sqlId` ile SQL delete/update yapilir.
   */
  async listGroupsLegacy(branchesId?: string): Promise<LegacyTaskGroup[]> {
    const groups = await this.listGroups(branchesId);
    return groups.map((g) => ({
      id: g.legacyFirebaseKey || String(g.id),
      sqlId: g.id,
      name: g.name,
      createdAt: g.createdAt,
      branchesId: normalizeBranchesId(g.branchesId ?? null),
    }));
  },

  // ── Ekip gruplari + uyeler ────────────────────────────────────────────
  async listTeamGroups(): Promise<TeamGroupDto[]> {
    const { data } = await taskApi.get(TEAMS);
    return unwrap<TeamGroupDto[]>(data, 'items');
  },

  async getTeamGroup(id: number): Promise<{ group: TeamGroupDto; members: TeamMemberDto[] } | null> {
    const { data } = await taskApi.get(`${TEAMS}/${id}`);
    if (!data?.success) return null;
    return { group: data.data, members: data.members ?? [] };
  },

  async createTeamGroup(input: {
    name: string;
    description?: string | null;
    color?: string | null;
    legacyFirebaseKey?: string | null;
    members?: TeamMemberInput[];
  }): Promise<TeamGroupDto> {
    const { data } = await taskApi.post(`${TEAMS}/create`, input);
    return unwrap<TeamGroupDto>(data, 'data');
  },

  async updateTeamGroup(input: {
    id: number;
    name?: string | null;
    description?: string | null;
    color?: string | null;
  }): Promise<TeamGroupDto> {
    const { data } = await taskApi.post(`${TEAMS}/update`, input);
    return unwrap<TeamGroupDto>(data, 'data');
  },

  async deleteTeamGroup(id: number): Promise<void> {
    await taskApi.post(`${TEAMS}/delete`, { id });
  },

  async getTeamMembers(teamGroupId: number): Promise<TeamMemberDto[]> {
    const { data } = await taskApi.get(`${TEAMS}/${teamGroupId}/members`);
    return unwrap<TeamMemberDto[]>(data, 'items');
  },

  /** Sirket geneli tum ekip uyeleri (tek cagri; ekip listesi avatar/rol ozeti icin). */
  async listAllTeamMembers(): Promise<TeamMemberDto[]> {
    const { data } = await taskApi.get(`${TEAMS}/all-members`);
    return unwrap<TeamMemberDto[]>(data, 'items');
  },

  async addTeamMember(teamGroupId: number, input: TeamMemberInput): Promise<TeamMemberDto> {
    const { data } = await taskApi.post(`${TEAMS}/${teamGroupId}/members`, input);
    return unwrap<TeamMemberDto>(data, 'data');
  },

  async removeTeamMember(
    teamGroupId: number,
    by: { memberId?: number; personnelFirebaseUid?: string }
  ): Promise<void> {
    await taskApi.post(`${TEAMS}/${teamGroupId}/members/remove`, by);
  },

  // ── Ekip projeleri + gorevleri ────────────────────────────────────────
  async listTeamProjects(params: {
    status?: string;
    teamGroupId?: number;
    branchId?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ items: TeamProjectDto[]; total: number; page: number; pageSize: number }> {
    const { data } = await taskApi.get(PROJECTS, { params });
    return {
      items: data?.items ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 50,
    };
  },

  async getTeamProject(id: number): Promise<TeamProjectDetail | null> {
    const { data } = await taskApi.get(`${PROJECTS}/${id}`);
    if (!data?.success) return null;
    return { project: data.data, tasks: data.tasks ?? [] };
  },

  async createTeamProject(input: {
    name: string;
    teamGroupId?: number | null;
    teamName?: string | null;
    isSequential?: boolean;
    groupId?: number | null;
    groupName?: string | null;
    branchId?: string | null;
    branchName?: string | null;
    legacyFirebaseKey?: string | null;
  }): Promise<TeamProjectDto> {
    const { data } = await taskApi.post(`${PROJECTS}/create`, input);
    return unwrap<TeamProjectDto>(data, 'data');
  },

  async updateTeamProject(id: number, input: {
    name?: string | null;
    teamGroupId?: number | null;
    teamName?: string | null;
    groupId?: number | null;
    groupName?: string | null;
    branchId?: string | null;
    branchName?: string | null;
    status?: string | null;
  }): Promise<TeamProjectDto> {
    const { data } = await taskApi.post(`${PROJECTS}/${id}/update`, input);
    return unwrap<TeamProjectDto>(data, 'data');
  },

  async deleteTeamProject(id: number): Promise<void> {
    await taskApi.post(`${PROJECTS}/${id}/delete`, {});
  },

  async getTeamProjectTasks(projectId: number): Promise<TeamProjectTaskDto[]> {
    const { data } = await taskApi.get(`${PROJECTS}/${projectId}/tasks`);
    return unwrap<TeamProjectTaskDto[]>(data, 'items');
  },

  /** Sirket geneli tum ekip proje gorevleri (tek cagri; liste maxPriority + gomulu tasks icin). */
  async listAllTeamProjectTasks(): Promise<TeamProjectTaskDto[]> {
    const { data } = await taskApi.get(`${PROJECTS}/all-tasks`);
    return unwrap<TeamProjectTaskDto[]>(data, 'items');
  },

  async createTeamProjectTask(projectId: number, input: {
    name: string;
    description?: string | null;
    teamMemberUid?: string | null;
    teamMemberName?: string | null;
    priority?: string | null;
    dueDate?: string | null;
    taskOrder?: number | null;
    imageUrls?: string | null;
    groupId?: number | null;
    branchId?: string | null;
    legacyFirebaseKey?: string | null;
  }): Promise<TeamProjectTaskDto> {
    const { data } = await taskApi.post(`${PROJECTS}/${projectId}/tasks/create`, input);
    return unwrap<TeamProjectTaskDto>(data, 'data');
  },

  async updateTeamProjectTask(projectId: number, taskId: number, input: {
    name?: string | null;
    description?: string | null;
    teamMemberUid?: string | null;
    teamMemberName?: string | null;
    priority?: string | null;
    dueDate?: string | null;
    imageUrls?: string | null;
    groupId?: number | null;
    branchId?: string | null;
    byUid?: string | null;
    byName?: string | null;
  }): Promise<TeamProjectTaskDto> {
    const { data } = await taskApi.post(`${PROJECTS}/${projectId}/tasks/${taskId}/update`, input);
    return unwrap<TeamProjectTaskDto>(data, 'data');
  },

  async deleteTeamProjectTask(projectId: number, taskId: number): Promise<void> {
    await taskApi.post(`${PROJECTS}/${projectId}/tasks/${taskId}/delete`, {});
  },

  /** action: start | complete | reopen. Sirali projede sira/kapi kurallari backend'de. */
  async changeTeamTaskStatus(projectId: number, taskId: number, input: {
    action: 'accept' | 'start' | 'pause' | 'resume' | 'complete' | 'reopen';
    byUid?: string | null;
    byName?: string | null;
  }): Promise<{ task: TeamProjectTaskDto; projectStatus: string; tasks: TeamProjectTaskDto[] }> {
    const { data } = await taskApi.post(`${PROJECTS}/${projectId}/tasks/${taskId}/status`, input);
    return {
      task: data?.data,
      projectStatus: data?.projectStatus,
      tasks: data?.tasks ?? [],
    };
  },

  async getTeamTaskHistory(projectId: number, taskId: number): Promise<TeamTaskStatusHistoryDto[]> {
    const { data } = await taskApi.get(`${PROJECTS}/${projectId}/tasks/${taskId}/history`);
    return unwrap<TeamTaskStatusHistoryDto[]>(data, 'items');
  },

  // ── Yillik planlar ────────────────────────────────────────────────────
  async listYearlyPlans(params: {
    status?: string;
    personnelFirebaseUid?: string;
    branchesId?: string;
    groupId?: number;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<{ items: YearlyPlanDto[]; total: number; page: number; pageSize: number }> {
    const { data } = await taskApi.get(YEARLY, { params });
    return {
      items: data?.items ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? 50,
    };
  },

  async getYearlyPlan(id: number): Promise<YearlyPlanDto | null> {
    const { data } = await taskApi.get(`${YEARLY}/${id}`);
    return unwrap<YearlyPlanDto | null>(data, 'data');
  },

  async createYearlyPlan(input: {
    name: string;
    description?: string | null;
    personnelFirebaseUid?: string | null;
    personnelName?: string | null;
    groupId?: number | null;
    groupName?: string | null;
    planDate?: string | null;
    yearlyPlanDetails?: string | null;
    branchesId?: string | null;
    branchName?: string | null;
    status?: string | null;
    legacyFirebaseKey?: string | null;
  }): Promise<YearlyPlanDto> {
    const { data } = await taskApi.post(`${YEARLY}/create`, input);
    return unwrap<YearlyPlanDto>(data, 'data');
  },

  async updateYearlyPlan(id: number, input: {
    name?: string | null;
    description?: string | null;
    personnelFirebaseUid?: string | null;
    personnelName?: string | null;
    groupId?: number | null;
    groupName?: string | null;
    planDate?: string | null;
    yearlyPlanDetails?: string | null;
    branchesId?: string | null;
    branchName?: string | null;
    status?: string | null;
  }): Promise<YearlyPlanDto> {
    const { data } = await taskApi.post(`${YEARLY}/${id}/update`, input);
    return unwrap<YearlyPlanDto>(data, 'data');
  },

  async deleteYearlyPlan(id: number): Promise<void> {
    await taskApi.post(`${YEARLY}/${id}/delete`, {});
  },
};

export default taskDataService;
